import { useCallback } from 'react';

import {
  Accordion,
  Box,
  Checkbox,
  FieldSet,
  FormField,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Text,
  Textarea,
  VisuallyHidden,
} from '@zillow/constellation-10';

import {
  emitDynamicNotifyLeadsEvent,
  EVENT_IDS,
  TRIGGER_SOURCE_MAP,
} from '../../../../analytics/events/deactivationModal';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type Props = {
  hideHeading?: boolean;
  leads: Array<{ name: string; isIncluded: boolean }>;
  message: string;
  setMessage: (message: string) => void;
  toggleIncludeLead: (index: number) => void;
  currentProperty?: ZrmClickstreamListing;
  user?: ZrmClickstreamUser;
};

const pluralizeWord = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural);

const NotifyLeads = ({
  hideHeading = false,
  leads = [],
  message = '',
  setMessage = () => {},
  toggleIncludeLead = () => {},
  currentProperty,
  user,
}: Props) => {
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    },
    [setMessage],
  );

  const numIncludedLeads = leads.filter(({ isIncluded }) => isIncluded).length;

  return (
    <Box css={{ marginTop: hideHeading ? '0' : '400', marginBottom: hideHeading ? '0' : '600' }}>
      {!hideHeading && <Text textStyle="heading-sm">Notify renters who you haven&apos;t responded to yet?</Text>}

      <Paragraph css={{ marginBlock: hideHeading ? '0' : '400', marginBottom: '400' }}>
        Our records show you haven&apos;t replied to {leads.length} other{' '}
        {pluralizeWord(leads.length, 'renter', 'renters')} who contacted you about this property. Consider sending a
        message to let them know it&apos;s no longer available so they can focus on other listings.
      </Paragraph>

      <FormField
        control={<Textarea onChange={onChange} value={message} rows={8} />}
        label={<Label>Customize your message below:</Label>}
      />

      <Accordion.Root css={{ marginBlock: '400' }}>
        <Accordion.Item value="notify-leads-recipients">
          <Accordion.Header>
            <Text textStyle="body-bold">Recipients</Text>
            <Text textStyle="body-sm">
              {numIncludedLeads} {pluralizeWord(numIncludedLeads, 'person', 'people')}
            </Text>
          </Accordion.Header>
          <Accordion.Panel>
            <Paragraph color="text.subtle" css={{ marginBlock: '400' }}>
              If you already responded to one of these renters, you can remove them from receiving this message.
            </Paragraph>
            <FieldSet
              legend={
                <VisuallyHidden asChild>
                  <Legend>Recipients</Legend>
                </VisuallyHidden>
              }
            >
              <Box>
                {leads.map(({ name, isIncluded }, i) => (
                  <LabeledControl
                    key={`lead-${i}`}
                    labelPosition="left"
                    fluid
                    css={{ marginBlock: '600' }}
                    label={<Label className="fs-mask">{name}</Label>}
                    control={
                      <Checkbox
                        onChange={() => {
                          toggleIncludeLead(i);

                          if (currentProperty && user) {
                            emitDynamicNotifyLeadsEvent({
                              user,
                              currentProperty,
                              triggerSource: isIncluded ? TRIGGER_SOURCE_MAP.DELETE : TRIGGER_SOURCE_MAP.ADD,
                            });
                          }
                        }}
                        checked={isIncluded}
                        data-eventid={EVENT_IDS.DYNAMIC_NOTIFY_LEADS}
                      />
                    }
                  />
                ))}
              </Box>
            </FieldSet>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    </Box>
  );
};

export default NotifyLeads;
